import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { getSocketUrl } from '../../lib/socket';
import { ACTIVE_SPEAKER_TUNING_DEFAULTS } from '@modelcast/types';
import { logVideoDiagnostic } from '../video/videoDiagnostics';
import type {
  ServerToClientEvents,
  ClientToServerEvents,
  RoomJoinData,
  UnifiedSessionState,
  RecordingState,
  BrowserAudioState,
  BrowserAudioSettings,
  BannedSessionParticipant,
  PoseTimerState,
  SessionParticipant,
  ScheduleItem,
  SessionArtworkFavoriteState,
  StageDisplayMode,
  ActiveSpeakerTuning,
  ViewerOrientation,
  ViewerOrientationStats,
  ChatMessage,
} from '@modelcast/types';
import { getSessionEndTargetForViewer } from './sessionNavigation';
import { clearStoredRoomSession, getRoomSessionDiagnosticContext } from './joinSession';

export interface RoomContextValue {
  roomCode: string;
  socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;

  // Unified session state - single source of truth
  sessionState: UnifiedSessionState | null;

  // Convenience accessors (derived from sessionState)
  isHost: boolean;
  hostParticipantIds: string[];
  originalHostId: string | null;
  modelParticipantId: string | null;
  modelProfileId: string | null;
  modelParticipantIds: string[];
  modelCameraLabels: Record<string, string>;
  participants: SessionParticipant[];
  bannedParticipants: BannedSessionParticipant[];
  remoteMutes: Record<string, string>;
  recording: RecordingState | null;
  browserAudio: BrowserAudioState | null;
  browserAudioSettings: BrowserAudioSettings;
  poseTimer: PoseTimerState | null;
  schedule: ScheduleItem[];
  isHighResStreaming: boolean;
  pinnedArtworkId: string | null;
  galleryArtworkFavorites: Record<string, SessionArtworkFavoriteState>;
  stagePinnedParticipantId: string | null;
  stageDisplayMode: StageDisplayMode;
  activeSpeakerTuning: ActiveSpeakerTuning;
  viewerOrientationStats: ViewerOrientationStats;
  startedAt: string | null;
  sessionName: string | null;
  chatMessages: ChatMessage[];
  chatTypingUsers: Map<string, string>;

  // Actions
  toggleHost: (participantId: string) => void;
  // Remove a participant and block their account from this event. The optional
  // reason is stored privately for the moderation record, never shown to the target.
  banParticipant: (participantId: string, reason: string | null, callback?: (response: { success: boolean; error?: string }) => void) => void;
  // Explicitly lift an event-level ban (targets the banned invite from state).
  unbanParticipant: (inviteId: string, callback?: (response: { success: boolean; error?: string }) => void) => void;
  setModelProfile: (profileId: string | null) => void;
  updateSchedule: (schedule: ScheduleItem[]) => void;
  updateBrowserAudioSettings: (settings: Partial<BrowserAudioSettings>) => void;
  pinArtwork: (artworkId: string) => void;
  clearPinnedArtwork: () => void;
  pinStageParticipant: (participantId: string) => void;
  clearStagePinnedParticipant: () => void;
  setStageDisplayMode: (mode: StageDisplayMode) => void;
  updateActiveSpeakerTuning: (patch: Partial<ActiveSpeakerTuning>) => void;
  resetActiveSpeakerTuning: () => void;
}

export const RoomContext = createContext<RoomContextValue | null>(null);
const EMPTY_VIEWER_ORIENTATION_STATS: ViewerOrientationStats = {
  portrait: 0,
  landscape: 0,
  total: 0,
  portraitPercent: 0,
  landscapePercent: 0,
};
const DEFAULT_BROWSER_AUDIO_SETTINGS: BrowserAudioSettings = {
  autoMuteArtistsDuringPoses: true,
  lowerMusicDuringBreaks: true,
};
const DEFAULT_STAGE_DISPLAY_MODE: StageDisplayMode = 'grid';

export function useRoom() {
  const context = useContext(RoomContext);
  if (!context) {
    throw new Error('useRoom must be used within a RoomProvider');
  }
  return context;
}

interface RoomProviderProps {
  roomCode: string;
  children: ReactNode;
}

export function RoomProvider({ roomCode, children }: RoomProviderProps) {
  const navigate = useNavigate();
  const [socket, setSocket] = useState<Socket<ServerToClientEvents, ClientToServerEvents> | null>(null);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatTypingUsers, setChatTypingUsers] = useState<Map<string, string>>(new Map());

  // Unified session state - single source of truth from server
  const [sessionState, setSessionState] = useState<UnifiedSessionState | null>(null);

  // Local isHost flag (set from join response and persisted locally)
  const [isHost, setIsHost] = useState(false);

  // Track if we've navigated due to session ending
  const hasNavigatedRef = useRef(false);
  const sessionStateRef = useRef<UnifiedSessionState | null>(null);

  // Derive values from sessionState for convenience
  const hostParticipantIds = sessionState?.hostParticipantIds ?? [];
  const originalHostId = sessionState?.originalHostId ?? null;
  const modelParticipantId = sessionState?.modelParticipantId ?? null;
  const modelProfileId = sessionState?.modelProfileId ?? null;
  const modelParticipantIds = sessionState?.modelParticipantIds ?? (modelParticipantId ? [modelParticipantId] : []);
  const modelCameraLabels = sessionState?.modelCameraLabels ?? {};
  const participants = sessionState?.participants ?? [];
  const bannedParticipants = sessionState?.bannedParticipants ?? [];
  const remoteMutes = sessionState?.remoteMutes ?? {};
  const recording = sessionState?.recording ?? null;
  const browserAudio = sessionState?.browserAudio ?? null;
  const browserAudioSettings = sessionState?.browserAudioSettings ?? DEFAULT_BROWSER_AUDIO_SETTINGS;
  const poseTimer = sessionState?.poseTimer ?? null;
  const schedule = sessionState?.schedule ?? [];
  const isHighResStreaming = sessionState?.isHighResStreaming ?? false;
  const pinnedArtworkId = sessionState?.pinnedArtworkId ?? null;
  const galleryArtworkFavorites = sessionState?.galleryArtworkFavorites ?? {};
  const stagePinnedParticipantId = sessionState?.stagePinnedParticipantId ?? null;
  const stageDisplayMode = sessionState?.stageDisplayMode ?? DEFAULT_STAGE_DISPLAY_MODE;
  const activeSpeakerTuning = sessionState?.activeSpeakerTuning ?? ACTIVE_SPEAKER_TUNING_DEFAULTS;
  const viewerOrientationStats = sessionState?.viewerOrientationStats ?? EMPTY_VIEWER_ORIENTATION_STATS;
  const startedAt = sessionState?.startedAt ?? null;
  const sessionName = sessionState?.sessionName ?? null;

  useEffect(() => {
    sessionStateRef.current = sessionState;
  }, [sessionState]);

  const clearRoomSession = useCallback(() => {
    clearStoredRoomSession(roomCode);
  }, [roomCode]);

  const showSessionUnavailable = useCallback(() => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;
    clearRoomSession();
    navigate(`/s/${roomCode}/unavailable`);
  }, [clearRoomSession, navigate, roomCode]);

  const getSessionEndTarget = useCallback((state: UnifiedSessionState | null) => {
    const myParticipantId = sessionStorage.getItem(`participant-id-${roomCode}`);
    return getSessionEndTargetForViewer(roomCode, myParticipantId, state);
  }, [roomCode]);

  const navigateToSessionSummary = useCallback((target?: string) => {
    if (hasNavigatedRef.current) return;
    const nextTarget = target ?? `/s/${roomCode}/summary`;

    console.log('[RoomProvider] Session ended, navigating to session-ended page');
    hasNavigatedRef.current = true;
    clearRoomSession();
    navigate(nextTarget);
  }, [clearRoomSession, navigate, roomCode]);
  
  // Connect to Socket.IO for session state and chat
  useEffect(() => {
    const participantId = sessionStorage.getItem(`participant-id-${roomCode}`);
    const participantName = sessionStorage.getItem(`participant-name-${roomCode}`) || 'Unknown';
    const participantRole = sessionStorage.getItem(`participant-role-${roomCode}`) || 'artist';
    const participantIsHost = sessionStorage.getItem(`participant-isHost-${roomCode}`) === 'true';
    const participantProfileId = sessionStorage.getItem(`participant-profileId-${roomCode}`);
    const socketUrl = getSocketUrl();

    setChatMessages([]);
    setChatTypingUsers(new Map());

    const newSocket: Socket<ServerToClientEvents, ClientToServerEvents> = io(socketUrl, {
      transports: ['websocket'],
    });

    newSocket.on('chat:history', (history) => {
      setChatMessages((currentMessages) => mergeChatMessages(currentMessages, history));
    });

    newSocket.on('chat:message', (message) => {
      setChatMessages((currentMessages) => appendUniqueChatMessage(currentMessages, message));
    });

    newSocket.on('chat:cleared', () => {
      setChatMessages([]);
      setChatTypingUsers(new Map());
    });

    newSocket.on('chat:typing', (typingParticipantId, typingParticipantName) => {
      setChatTypingUsers((currentTypingUsers) => {
        const nextTypingUsers = new Map(currentTypingUsers);
        nextTypingUsers.set(typingParticipantId, typingParticipantName);
        return nextTypingUsers;
      });
    });

    newSocket.on('chat:stopped-typing', (typingParticipantId) => {
      setChatTypingUsers((currentTypingUsers) => {
        const nextTypingUsers = new Map(currentTypingUsers);
        nextTypingUsers.delete(typingParticipantId);
        return nextTypingUsers;
      });
    });

    newSocket.on('connect', () => {
      console.log('[RoomProvider] Socket connected');
      console.log('[RoomProvider] Session storage - participantIsHost:', participantIsHost, 'participantId:', participantId);
      if (participantId) {
        const joinData: RoomJoinData = {
          participantId,
          displayName: participantName,
          role: participantRole as 'model' | 'artist',
          isHost: participantIsHost,
          profileId: participantProfileId || null,
        };

        console.log('[RoomProvider] Emitting room:join with isHost:', participantIsHost);
        newSocket.emit('room:join', roomCode, joinData, (response) => {
          if (response.success) {
            console.log('[RoomProvider] Socket joined room successfully, isHost:', response.isHost);
            logVideoDiagnostic('info', 'socket-connected', {
              socketId: newSocket.id ?? null,
              joinedRoom: true,
              isHost: response.isHost,
              profileId: participantProfileId || null,
            }, getRoomSessionDiagnosticContext(roomCode));
            setIsHost(response.isHost);
            // Only expose socket after successfully joining
            console.log('[RoomProvider] Setting socket in context');
            setSocket(newSocket);
          } else {
            console.error('[RoomProvider] Failed to join room via socket:', response.error);
            if (response.error === 'Session no longer available' || response.error === 'You were removed by the organizer') {
              showSessionUnavailable();
              return;
            }
            if (response.error === 'Session not open yet') {
              clearRoomSession();
              alert('This session opens five minutes before it starts.');
              navigate(`/s/${roomCode}`, { replace: true });
              return;
            }
            // Handle room full or other join errors
            if (response.error === 'Room is full') {
              alert('This room is full. The maximum number of participants has been reached.');
              navigate('/');
            }
          }
        });
      } else {
        console.warn('[RoomProvider] No participantId in session storage, cannot join room');
      }
    });

    // Handle unified session state - single event for all state changes
    newSocket.on('session:state', (state: UnifiedSessionState) => {
      console.log('[RoomProvider] Received session:state v' + state.version, {
        status: state.status,
        startedAt: state.startedAt,
        participantCount: state.participants.length,
        bannedParticipantCount: state.bannedParticipants?.length ?? 0,
        hostIds: state.hostParticipantIds,
        modelId: state.modelParticipantId,
        modelProfileId: state.modelProfileId,
        modelCameraIds: state.modelParticipantIds,
        hasPoseTimer: !!state.poseTimer,
        browserAudio: state.browserAudio,
        browserAudioSettings: state.browserAudioSettings,
        pinnedArtworkId: state.pinnedArtworkId,
        stagePinnedParticipantId: state.stagePinnedParticipantId,
        stageDisplayMode: state.stageDisplayMode,
        activeSpeakerTuning: state.activeSpeakerTuning,
        viewerOrientationStats: state.viewerOrientationStats,
      });

      setSessionState(state);

      const myParticipantId = sessionStorage.getItem(`participant-id-${roomCode}`);
      const myProfileId = sessionStorage.getItem(`participant-profileId-${roomCode}`);

      // Derived from central state (no direct moderation socket events): if this
      // account is in the broadcast banned list, leave and show the generic
      // removed message.
      const amIBanned = (state.bannedParticipants ?? []).some((banned) =>
        (myParticipantId !== null && banned.participantIds.includes(myParticipantId)) ||
        (myProfileId !== null && banned.profileId !== null && banned.profileId === myProfileId)
      );
      if (amIBanned) {
        console.log('[RoomProvider] This account is banned from the event; leaving the room');
        showSessionUnavailable();
        return;
      }

      // Update isHost if the current user's host status changed
      const amIHost = myParticipantId ? state.hostParticipantIds.includes(myParticipantId) : false;
      setIsHost(amIHost);

      // React to status changes
      if (state.status === 'ended' && !hasNavigatedRef.current) {
        navigateToSessionSummary(getSessionEndTarget(state));
      } else if (state.status === 'closed' && !hasNavigatedRef.current) {
        console.log('[RoomProvider] Session closed, navigating to home');
        hasNavigatedRef.current = true;
        clearRoomSession();
        navigate('/');
      }
    });

    newSocket.on('session:unavailable', () => {
      console.log('[RoomProvider] Session is no longer available to this participant');
      showSessionUnavailable();
    });

    // DEPRECATED: These events are being phased out in favor of session:state
    // Keeping temporarily for backwards compatibility during migration
    newSocket.on('session:ended', (data) => {
      if (hasNavigatedRef.current) return;
      console.log('[RoomProvider] Received deprecated session:ended event:', data.message);
      navigateToSessionSummary(getSessionEndTarget(sessionStateRef.current));
    });

    return () => {
      newSocket.emit('room:leave');
      newSocket.close();
    };
  }, [roomCode, navigate, clearRoomSession, showSessionUnavailable, navigateToSessionSummary, getSessionEndTarget]);

  useEffect(() => {
    if (!socket) return;

    let pendingTimeout: number | null = null;
    let lastOrientation: ViewerOrientation | null = null;

    const emitOrientation = () => {
      pendingTimeout = null;
      const nextOrientation = getViewportOrientation();
      if (lastOrientation === nextOrientation) return;

      lastOrientation = nextOrientation;
      socket.emit('viewer:orientation', nextOrientation);
    };

    const scheduleEmit = () => {
      if (pendingTimeout !== null) {
        window.clearTimeout(pendingTimeout);
      }
      pendingTimeout = window.setTimeout(emitOrientation, 150);
    };

    emitOrientation();
    window.addEventListener('resize', scheduleEmit);
    window.addEventListener('orientationchange', scheduleEmit);
    window.visualViewport?.addEventListener('resize', scheduleEmit);

    return () => {
      if (pendingTimeout !== null) {
        window.clearTimeout(pendingTimeout);
      }
      window.removeEventListener('resize', scheduleEmit);
      window.removeEventListener('orientationchange', scheduleEmit);
      window.visualViewport?.removeEventListener('resize', scheduleEmit);
    };
  }, [socket]);

  const setModelProfile = (profileId: string | null) => {
    // The server authorizes organizer/model access; clients should not derive
    // active model state locally.
    socket?.emit('room:set-model', profileId);
  };

  const toggleHost = (participantId: string) => {
    // Only current host can add/remove co-hosts
    if (!isHost) {
      console.log('Only a host can add/remove co-hosts');
      return;
    }
    socket?.emit('room:toggle-host', participantId);
  };

  const banParticipant = useCallback((participantId: string, reason: string | null, callback?: (response: { success: boolean; error?: string }) => void) => {
    if (!socket) {
      callback?.({ success: false, error: 'Not connected to the session' });
      return;
    }
    socket.emit('room:ban', { participantId, reason }, callback);
  }, [socket]);

  const unbanParticipant = useCallback((inviteId: string, callback?: (response: { success: boolean; error?: string }) => void) => {
    if (!socket) {
      callback?.({ success: false, error: 'Not connected to the session' });
      return;
    }
    socket.emit('room:unban', inviteId, callback);
  }, [socket]);

  const updateSchedule = useCallback((newSchedule: ScheduleItem[]) => {
    // Only host or model can update schedule
    const myParticipantId = sessionStorage.getItem(`participant-id-${roomCode}`);
    const isCurrentModel = myParticipantId ? modelParticipantIds.includes(myParticipantId) : false;

    if (!isHost && !isCurrentModel) {
      console.log('Only the host or model can update the schedule');
      return;
    }
    console.log('[RoomProvider] Updating schedule:', newSchedule.length, 'items');
    socket?.emit('schedule:update', newSchedule);
  }, [socket, isHost, modelParticipantIds, roomCode]);

  const updateBrowserAudioSettings = useCallback((settings: Partial<BrowserAudioSettings>) => {
    if (!isHost) {
      console.log('Only a host can update browser audio settings');
      return;
    }

    socket?.emit('browser-audio:settings-update', settings);
  }, [isHost, socket]);

  const pinArtwork = useCallback((artworkId: string) => {
    if (!isHost) {
      console.log('Only a host can spotlight artwork');
      return;
    }
    socket?.emit('gallery:pin-artwork', artworkId);
  }, [isHost, socket]);

  const clearPinnedArtwork = useCallback(() => {
    if (!isHost) {
      console.log('Only a host can clear the artwork spotlight');
      return;
    }
    socket?.emit('gallery:pin-artwork', null);
  }, [isHost, socket]);

  const pinStageParticipant = useCallback((participantId: string) => {
    if (!isHost) {
      console.log('Only a host can spotlight a participant');
      return;
    }
    socket?.emit('stage:pin-participant', participantId);
  }, [isHost, socket]);

  const clearStagePinnedParticipant = useCallback(() => {
    if (!isHost) {
      console.log('Only a host can clear the participant spotlight');
      return;
    }
    socket?.emit('stage:pin-participant', null);
  }, [isHost, socket]);

  const setStageDisplayMode = useCallback((mode: StageDisplayMode) => {
    socket?.emit('stage:set-display-mode', mode);
  }, [socket]);

  const updateActiveSpeakerTuning = useCallback((patch: Partial<ActiveSpeakerTuning>) => {
    socket?.emit('stage:set-active-speaker-tuning', patch);
  }, [socket]);

  const resetActiveSpeakerTuning = useCallback(() => {
    socket?.emit('stage:set-active-speaker-tuning', ACTIVE_SPEAKER_TUNING_DEFAULTS);
  }, [socket]);

  return (
    <RoomContext.Provider
      value={{
        roomCode,
        socket,
        sessionState,
        isHost,
        hostParticipantIds,
        originalHostId,
        modelParticipantId,
        modelProfileId,
        modelParticipantIds,
        modelCameraLabels,
        participants,
        bannedParticipants,
        remoteMutes,
        recording,
        browserAudio,
        browserAudioSettings,
        poseTimer,
        schedule,
        isHighResStreaming,
        pinnedArtworkId,
        galleryArtworkFavorites,
        stagePinnedParticipantId,
        stageDisplayMode,
        activeSpeakerTuning,
        viewerOrientationStats,
        startedAt,
        sessionName,
        chatMessages,
        chatTypingUsers,
        toggleHost,
        banParticipant,
        unbanParticipant,
        setModelProfile,
        updateSchedule,
        updateBrowserAudioSettings,
        pinArtwork,
        clearPinnedArtwork,
        pinStageParticipant,
        clearStagePinnedParticipant,
        setStageDisplayMode,
        updateActiveSpeakerTuning,
        resetActiveSpeakerTuning,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
}

function appendUniqueChatMessage(messages: ChatMessage[], message: ChatMessage) {
  if (messages.some((currentMessage) => currentMessage.id === message.id)) {
    return messages;
  }

  return [...messages, message].slice(-100);
}

function mergeChatMessages(currentMessages: ChatMessage[], incomingMessages: ChatMessage[]) {
  const messagesById = new Map<string, ChatMessage>();

  for (const message of [...currentMessages, ...incomingMessages]) {
    messagesById.set(message.id, message);
  }

  return Array.from(messagesById.values())
    .sort((firstMessage, secondMessage) => (
      new Date(firstMessage.createdAt).getTime() - new Date(secondMessage.createdAt).getTime()
    ))
    .slice(-100);
}

function getViewportOrientation(): ViewerOrientation {
  const viewport = window.visualViewport;
  const width = viewport?.width ?? window.innerWidth;
  const height = viewport?.height ?? window.innerHeight;

  return height >= width ? 'portrait' : 'landscape';
}
